import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Clock, MapPin, FileText, Trash2 } from 'lucide-react';
import { CalendarEvent } from '../../../hooks/useSupabaseEvents';
import { useTimeFormat, useDateFormat, formatShortDate } from '../../settings/TimePicker';

interface ImportedEventDetailProps {
  isOpen: boolean;
  event: CalendarEvent | null;
  position: { x: number; y: number; barRect?: DOMRect } | null;
  calendarName?: string;
  calendarColor?: string;
  onDelete?: () => void;
  onClose: () => void;
  readOnly?: boolean;
}

export const ImportedEventDetail: React.FC<ImportedEventDetailProps> = ({
  isOpen,
  event,
  position,
  calendarName,
  calendarColor,
  onDelete,
  onClose,
  readOnly = false,
}) => {
  const timeFormat = useTimeFormat();
  const dateFormat = useDateFormat();
  const panelRef = useRef<HTMLDivElement>(null);
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const dragOffset = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Reset drag position when event changes
  useEffect(() => { setDragPos(null); }, [event?.id]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragOffset.current) return;
      const padding = 16;
      const panel = panelRef.current;
      const pw = panel?.offsetWidth ?? 320;
      const ph = panel?.offsetHeight ?? 300;
      const x = Math.max(padding, Math.min(e.clientX - dragOffset.current.x, window.innerWidth - pw - padding));
      const y = Math.max(padding, Math.min(e.clientY - dragOffset.current.y, window.innerHeight - ph - padding));
      setDragPos({ x, y });
    };
    const onUp = () => { dragOffset.current = null; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  if (!isOpen || !event) return null;

  const formatTime = (timeStr: string) => {
    if (timeFormat === '24h') return timeStr.substring(0, 5);
    return new Date(`2000-01-01T${timeStr}`)
      .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
      .replace(' ', '')
      .toLowerCase();
  };

  const formatDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const weekday = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short' });
    return `${weekday}, ${formatShortDate(dateStr, dateFormat)} ${y}`;
  };

  const startDate = event.dates[0];
  const endDate = event.dates.length > 1 ? event.dates[event.dates.length - 1] : null;
  const isMultiDay = endDate && endDate !== startDate;

  // Positioning
  const calculateStyle = (): React.CSSProperties => {
    const mw = Math.min(320, window.innerWidth - 32);
    const padding = 16;
    const maxH = window.innerHeight - padding * 2;

    if (!position) {
      return { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: `${mw}px`, maxHeight: `${maxH}px` };
    }

    let left = 0;
    let top = 0;

    if (position.barRect) {
      const barRect = position.barRect;
      const spaceRight = window.innerWidth - barRect.right;
      const gap = 12;

      if (spaceRight >= mw + gap + padding) {
        left = barRect.right + gap;
      } else if (barRect.left >= mw + gap + padding) {
        left = barRect.left - mw - gap;
      } else {
        left = Math.max(padding, (window.innerWidth - mw) / 2);
      }
      top = barRect.top;
    } else {
      left = position.x;
      top = position.y;
    }

    left = Math.max(padding, Math.min(left, window.innerWidth - mw - padding));
    top = Math.max(padding, Math.min(top, window.innerHeight - padding));

    // Clamp so panel doesn't overflow bottom
    const availableH = window.innerHeight - top - padding;
    const appliedMaxH = Math.min(maxH, Math.max(availableH, 200));

    return { left: `${left}px`, top: `${top}px`, width: `${mw}px`, maxHeight: `${appliedMaxH}px` };
  };

  const panelStyle = (): React.CSSProperties => {
    if (dragPos) {
      const mw = Math.min(320, window.innerWidth - 32);
      const padding = 16;
      const maxH = window.innerHeight - padding * 2;
      const availableH = window.innerHeight - dragPos.y - padding;
      return { left: `${dragPos.x}px`, top: `${dragPos.y}px`, width: `${mw}px`, maxHeight: `${Math.min(maxH, Math.max(availableH, 200))}px` };
    }
    return calculateStyle();
  };

  const iconCls = "w-4 h-4 text-[var(--content-tertiary)] flex-shrink-0 mt-0.5";

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-[var(--modal-overlay)] animate-fadeInBackdrop"
      onMouseDown={(e) => {
        if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        className="fixed bg-[var(--modal-bg)] rounded-2xl shadow-xl border border-[var(--border-primary)] animate-fadeIn overflow-hidden flex flex-col"
        style={panelStyle()}
      >
        {/* Header — drag handle */}
        <div
          className="px-4 pt-3.5 pb-3 flex items-start gap-3 cursor-grab active:cursor-grabbing select-none"
          onMouseDown={(e) => {
            if ((e.target as HTMLElement).closest('button')) return;
            const rect = panelRef.current?.getBoundingClientRect();
            if (!rect) return;
            dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
          }}
        >
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-semibold text-[var(--content-primary)] leading-snug">
              {event.title}
            </h3>
            {calendarName && (
              <div className="flex items-center gap-1.5 mt-1">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${calendarColor || 'bg-gray-500'}`} />
                <span className="text-[11px] text-[var(--content-tertiary)]">{calendarName}</span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[var(--sidebar-hover)] rounded-lg transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-[var(--content-tertiary)]" />
          </button>
        </div>

        {/* Details */}
        <div className="px-4 pb-3 space-y-2.5 overflow-y-auto min-h-0 flex-1">
          {/* Date */}
          <div className="flex items-start gap-2.5">
            <Calendar className={iconCls} />
            <span className="text-xs text-[var(--content-primary)]">
              {formatDate(startDate)}
              {isMultiDay && ` — ${formatDate(endDate)}`}
            </span>
          </div>

          {/* Time */}
          {!event.all_day && event.start_time && (
            <div className="flex items-start gap-2.5">
              <Clock className={iconCls} />
              <span className="text-xs text-[var(--content-primary)]">
                {formatTime(event.start_time)}
                {event.end_time && ` – ${formatTime(event.end_time)}`}
              </span>
            </div>
          )}

          {/* Location */}
          {(event as any).location && (
            <div className="flex items-start gap-2.5">
              <MapPin className={iconCls} />
              <span className="text-xs text-[var(--content-primary)]">{(event as any).location}</span>
            </div>
          )}

          {/* Notes */}
          {event.notes && (
            <div className="flex items-start gap-2.5">
              <FileText className={iconCls} />
              <p className="text-xs text-[var(--content-secondary)] whitespace-pre-wrap leading-relaxed">{event.notes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-[var(--border-secondary)] flex items-center justify-between">
          <span className="text-[10px] text-[var(--content-tertiary)] uppercase tracking-wide">
            {readOnly ? 'Shared event' : 'Imported event'}
          </span>
          {!readOnly && onDelete && (
            <button
              onClick={onDelete}
              className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-[var(--error)] hover:bg-[var(--status-error-bg)] rounded-lg transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              Remove
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
